import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Input,
  LoadingIcon,
  Modal,
  Tooltip,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  SIGN_IN_DIALOG_CLOSE,
  SIGN_IN_DIALOG_PASSWORD,
  SIGN_IN_DIALOG_SSO_REQUIRED,
  SIGN_IN_DIALOG_SUBMIT,
  SIGN_IN_DIALOG_SUBMIT_LOADING,
  SIGN_IN_DIALOG_USERNAME,
} from 'src/ui/constants/testIds';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { clearConnection } from 'src/ui/features/connection';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useEditConnectionMutation } from 'src/ui/graph/types';
import {
  useCurrentConnection,
  useVerifyConnection,
} from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { editIndexSelector } from 'src/ui/selectors/connection';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';
import { isSsoConnectionRequired } from 'src/ui/utils/features';

const isOpenSelector = isDialogOpenSelector(DialogType.SIGN_IN);

export const SignInDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const isOpen = useAppSelector(isOpenSelector);
  const editIndex = useAppSelector(editIndexSelector);
  const { connection } = useCurrentConnection(editIndex.toString(), org);
  const invalidateConnection = useInvalidateConnection(
    org,
    connection?.id,
    connection?.index
  );

  const {
    isLoading: isValidating,
    mutate: validate,
    reset: resetValidation,
  } = useVerifyConnection();
  const {
    isLoading: isEditing,
    mutate: editConnection,
  } = useEditConnectionMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: () => {
      invalidateConnection();
      dispatch(closedDialog());
      dispatch(clearConnection());
    },
  });

  const [value, setValue] = React.useState('');
  const ssoRequired = isSsoConnectionRequired();

  const handleClose = () => {
    dispatch(closedDialog());
    resetValidation();
    setValue('');
  };

  const handleSubmit = () => {
    if (!value || !connection) {
      return;
    }

    // Test connection with new password, it is important that we remove the
    // existing token if there is one because the Stardog.js client will prefer
    // using that and we'll just get a 401 making the user super mad and throw
    // stuff. We don't want to be responsible for broken screens or coffee mugs.
    const newConnection = {
      ...connection,
      token: undefined,
      password: value,
      useSSO: false,
    };

    validate(newConnection, {
      onError,
      onSuccess: (data) => {
        // We need to add in the connection ID to satisfy the edit input
        // and not duplicate all the verification logic with different types.
        editConnection({
          input: {
            id: connection.id as string,
            org_domain: org,
            token: data.token,
            useSSO: false,
          },
        });
      },
    });
  };

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      closeIconTestId={SIGN_IN_DIALOG_CLOSE}
      footerActions={
        <Tooltip
          description={
            copy.components.endpoints.inputs.password.validationRequired
          }
          disabled={Boolean(value) || ssoRequired}
        >
          <Button
            disabled={!value || ssoRequired}
            leftIcon={isValidating || isEditing ? LoadingIcon : undefined}
            leftIconProps={{ testId: SIGN_IN_DIALOG_SUBMIT_LOADING }}
            onClick={handleSubmit}
            size="sm"
            testId={SIGN_IN_DIALOG_SUBMIT}
            variant="primary-accent"
          >
            {copy.components.endpoints.signInDialog.buttons.submit}
          </Button>
        </Tooltip>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.endpoints.signInDialog.title}
    >
      <div className="endpoint-dialog-body">
        <div className="endpoint-dialog-header">
          <Body size="lg">{copy.components.endpoints.signInDialog.header}</Body>
          <Body size="lg" weight="semibold">
            {connection?.name}
          </Body>
          <Body size="md">{connection?.endpoint}</Body>
        </div>

        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            clearable={false}
            disabled
            id="sd-connection-username"
            label={copy.components.endpoints.inputs.username.label}
            size="md"
            testId={SIGN_IN_DIALOG_USERNAME}
            value={connection?.username || ''}
          />

          {ssoRequired ? (
            <Body size="md" testId={SIGN_IN_DIALOG_SSO_REQUIRED}>
              {copy.components.endpoints.signInDialog.ssoRequired}
            </Body>
          ) : (
            <Input
              autoComplete="password"
              autoFocus
              clearable={false}
              id="sd-connection-password"
              label={copy.components.endpoints.inputs.password.label}
              onChange={(e) => setValue(e.target.value)}
              size="md"
              testId={SIGN_IN_DIALOG_PASSWORD}
              type="password"
              value={value}
            />
          )}

          <input hidden type="submit" />
        </form>
      </div>
    </Modal>
  );
};
